import { faDownload, faTrash } from '@fortawesome/free-solid-svg-icons'
import React, { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { DateTime } from 'luxon'
import { useSearchParams } from 'react-router-dom'
import { Content, Data, Layout, Navigation, Modal, Responsive, theme } from 'ui'
import { flat, truncate } from 'utils'
import {
  useReports,
  useDownloadReport,
  useDeleteReport,
} from '../../../queries'

const { Table, Cell, Row, Header } = Data

export const ReportListView: React.FC = () => {
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('expanded')

  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = parseInt(searchParams.get('page') || '1', 10)

  const setCurrentPage = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    setSearchParams(newParams)
  }

  const { data, isLoading: loading } = useReports(currentPage)

  // Handle the actual API response structure
  const reports = (data as any)?.reports ?? []
  const totalPages = (data as any)?.total_pages ?? 0
  const total = (data as any)?.total ?? 0

  const sections = [
    {
      title: 'Profile Reports',
      reports: reports.filter((r: any) => r.index_type === 'PROFILES'),
    },
    {
      title: 'Event Reports',
      reports: reports.filter((r: any) => r.index_type === 'EVENTS'),
    },
    {
      title: 'Invitation Reports',
      reports: reports.filter((r: any) => r.index_type === 'INVITATIONS'),
    },
  ]
  const allReports = flat(sections.map((s: any) => s.reports))
  const [reportToDestroyId, setSearchToDestroy] = useState(
    null as string | null
  )
  const handleDeleteClick = (id?: string) => () => {
    setSearchToDestroy(id as string)
  }
  const deleteReportMutation = useDeleteReport()
  const handleDeleteReport = async () => {
    if (reportToDestroyId) {
      try {
        await deleteReportMutation.mutateAsync(reportToDestroyId)
        setSearchToDestroy(null)
        // If we're on the last page and deleting the last item, go back a page
        if (reports.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1)
        }
      } catch (error) {
        console.error('Failed to delete report:', error)
      }
    }
  }
  const dismissModal = () => setSearchToDestroy(null)

  const downloadReportMutation = useDownloadReport()
  const handleDownloadClick = (id?: string) => async () => {
    if (id) {
      try {
        const result = await downloadReportMutation.mutateAsync(id)
        if (result.downloadUrl) {
          window.location.href = result.downloadUrl
        }
      } catch (error) {
        console.error('Failed to download report:', error)
      }
    }
  }

  return (
    <Layout.PaddedContent className="flex-grow w-full overflow-y-auto bg-white">
      <Helmet>
        <title>Reports - MCSS Pro Connect</title>
      </Helmet>
      <div className="flex-col flex w-full max-w-7xl mx-auto pb-32">
        {/* Header with total count */}
        {total > 0 && !loading && (
          <div className="mb-6 text-sm text-gray-600">
            Showing {reports.length} of {total} total reports
            {currentPage > 1 && ` (page ${currentPage} of ${totalPages})`}
          </div>
        )}

        {loading && (
          <div className="mr-4 text-sm flex items-center text-gray-primary bg-white rounded-full shadow-sm p-4 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Loading reports...
          </div>
        )}
        {sections.map((section) => (
          <div
            key={section.title}
            className="flex-col flex w-full mb-8"
            style={{
              opacity: loading ? 0.6 : 1,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            <div className="flex-col flex sm:flex-row flex mb-4">
              <div className="w-full">
                <Content.Heading>{section.title}</Content.Heading>
              </div>
            </div>
            {section.reports.length > 0 && (
              <Table>
                <Row>
                  <Header
                    width="30%"
                    className="!text-secondary !bg-transparent font-bold"
                  >
                    Title
                  </Header>
                  <Header
                    width="35%"
                    className="!text-secondary !bg-transparent font-bold"
                  >
                    Description
                  </Header>
                  <Header
                    width="20%"
                    className="!text-actionable !bg-transparent font-bold"
                  >
                    Created
                  </Header>
                  <Header
                    width="15%"
                    className="!text-secondary !bg-transparent text-right pr-4"
                  >
                    Actions
                  </Header>
                </Row>
                {section.reports.map((report: any) => (
                  <Row key={report?.id}>
                    <Cell width="30%">
                      <div className="font-medium">
                        {truncate(report?.name ?? '', 50)}
                      </div>
                    </Cell>
                    <Cell width="35%">
                      <div className="text-gray-600">
                        {report?.description ? (
                          truncate(report.description, 80)
                        ) : (
                          <span className="text-gray-400 italic">
                            No description
                          </span>
                        )}
                      </div>
                    </Cell>
                    <Cell width="20%">
                      <div className="text-gray-500 text-sm">
                        {report?.created_at
                          ? DateTime.fromISO(report.created_at).toFormat(
                              'MM/dd/yyyy'
                            )
                          : '-'}
                      </div>
                    </Cell>
                    <Cell width="15%">
                      <div className="flex justify-end items-center space-x-2 pr-2">
                        <Navigation.IconButton
                          color={theme.colorActionable}
                          icon={faDownload}
                          onClick={handleDownloadClick(report?.id)}
                          title="Download Report"
                        />
                        <Navigation.IconButton
                          color={theme.colorDestructive}
                          icon={faTrash}
                          onClick={handleDeleteClick(report?.id)}
                          title="Delete Report"
                        />
                      </div>
                    </Cell>
                  </Row>
                ))}
              </Table>
            )}
            {section.reports.length === 0 && !loading && (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                No {section.title.toLowerCase()} found.
              </div>
            )}
          </div>
        ))}

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div className="mt-6 flex justify-center">
            <Navigation.SimplePaginationBar
              onPageClick={setCurrentPage}
              currentPage={currentPage}
              pageCount={totalPages}
            />
          </div>
        )}
      </div>
      {reportToDestroyId ? (
        <Modal.Dialog
          title="Delete Report"
          message={`Are you sure you want to delete the report "${
            allReports?.find((s) => s?.id === reportToDestroyId)?.name ?? ''
          }"?`}
          destructive={true}
          onDismiss={dismissModal}
          onConfirm={handleDeleteReport}
          loading={deleteReportMutation.isPending}
        />
      ) : null}
    </Layout.PaddedContent>
  )
}
